import React from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSpace } from '../../contexts/SpaceContext';
import API_BASE_URL, { API_ENDPOINTS } from '../../config';
import CollectionSelector, { CollectionType } from './CollectionSelector';
import CollectionModal from '../CollectionModal';
import CollectionShareModal from '../CollectionShareModal';
import ModalCloseButton from '../ModalCloseButton';
import BindleTermResolveDialog from './BindleTermResolveDialog';
import { resolveLooseTerms, applyTermConversions, ResolvedTerm } from '../../utils/resolveBindleTerms';
import { toggleSearchOperator, hasAndOperator } from '../../utils/searchParser';

import '../../styles/components/menuBar.css';
import '../../styles/components/unifiedInventoryMenuBar.css';

interface UnifiedInventoryMenuBarProps {
  // Collection state
  collection: CollectionType;
  onCollectionChange: (collection: CollectionType) => void;
  collectionCounts: Record<CollectionType, number>;
  onCollectionsRefresh?: () => void;
  
  // Mode state
  isSelectMode: boolean;
  onSelectModeToggle: () => void;
  
  // Search
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onVoiceListeningChange?: (isListening: boolean) => void;
  filteredItemsCount?: number;
  collectionId?: number; // For scoping bindles to a collection
  collectionPermission?: string; // 'view', 'edit', 'full_edit', or undefined (owner)
  onActiveBindleChange?: (bindle: { name: string; description: string | null; search_string: string } | null) => void;
  
  // Filter/Display
  onFilterClick: () => void;
  onDisplayClick: () => void;
  hasActiveFilters?: boolean;
  
  // Selection state (for Manage mode)
  selectedCount: number;
  selectedItems: number[];
  onSelectAll?: () => void;
  onClearSelection?: () => void;
  onAction?: (action: string, itemIds: number[]) => void;
  
  // Voice command support
  items?: Array<{ id: number; name: string; checked?: boolean }>;
  isChecklistMode?: boolean;
  isStockKeeping?: boolean;
  onToggleItem?: (itemId: number) => void;
  onCheckAll?: () => void;
  onUncheckAll?: () => void;
  
  // Register voice toggle for direct invocation (preserves user gesture for iOS)
  onRegisterVoiceToggle?: (toggleFn: () => void) => void;

  // Kitchen collection sharing
  kitchenCollectionId?: number | null;
}

const UnifiedInventoryMenuBar: React.FC<UnifiedInventoryMenuBarProps> = ({
  collection,
  onCollectionChange,
  collectionCounts,
  onCollectionsRefresh,
  isSelectMode,
  onSelectModeToggle,
  searchTerm,
  onSearchChange,
  onVoiceListeningChange,
  filteredItemsCount = 0,
  collectionId,
  collectionPermission,
  onActiveBindleChange,
  onFilterClick,
  onDisplayClick,
  hasActiveFilters = false,
  selectedCount,
  selectedItems,
  onSelectAll,
  onClearSelection,
  onAction,
  items = [],
  isChecklistMode = false,
  isStockKeeping = false,
  onToggleItem,
  onCheckAll,
  onUncheckAll,
  onRegisterVoiceToggle,
  kitchenCollectionId,
}) => {
  const { token, user } = useAuth();
  const { activeSpace } = useSpace();
  const isAdmin = user?.role === 'admin';
  const [showManageMenu, setShowManageMenu] = React.useState(false);
  const [showCollectionSubmenu, setShowCollectionSubmenu] = React.useState(false);
  const [isListening, setIsListening] = React.useState(false);
  const [isSearchFocused, setIsSearchFocused] = React.useState(false);
  
  const canMoveRemoveFromCurrentCollection =
    collection !== 'all' &&
    !collection.toString().startsWith('merged-') &&
    collection !== 'shared_with_me' &&
    !collection.toString().startsWith('shared_with_group_');
  
  
  // Notify parent when listening state changes
  React.useEffect(() => {
    onVoiceListeningChange?.(isListening);
  }, [isListening, onVoiceListeningChange]);

  React.useEffect(() => {
    if (!showManageMenu) {
      setShowCollectionSubmenu(false);
    }
  }, [showManageMenu]);
  const recognitionRef = React.useRef<any>(null);
  const isListeningRef = React.useRef(false);
  const lastSpeechTimeRef = React.useRef<number>(0);
  const lastTranscriptRef = React.useRef<string>('');
  const retryTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastSearchTermRef = React.useRef<string>('');
  const retriedTermsRef = React.useRef<Set<string>>(new Set());
  const searchStartTimeRef = React.useRef<number>(0);
  
  // Voice commands that should be stripped from search
  const VOICE_COMMANDS = ['ok', 'okay', 'done', 'search', 'go', 'find', 'enter'];
  
  // Simple stemming: normalize plural/singular forms
  const normalizeTerm = (term: string): string[] => {
    const lower = term.toLowerCase().trim();
    const variations = [lower];
    
    // Handle common plural patterns - only add ONE variation to avoid loops
    if (lower.endsWith('ies')) {
      variations.push(lower.slice(0, -3) + 'y'); // strawberries → strawberry
    } else if (lower.endsWith('es') && lower.length > 3) {
      variations.push(lower.slice(0, -2)); // boxes → box
    } else if (lower.endsWith('s') && lower.length > 3 && !lower.endsWith('ss')) {
      variations.push(lower.slice(0, -1)); // bananas → banana
    } else if (lower.endsWith('y') && lower.length > 3) {
      variations.push(lower.slice(0, -1) + 'ies'); // strawberry → strawberries
    } else if (!lower.endsWith('s')) {
      variations.push(lower + 's'); // banana → bananas
    }
    
    return variations;
  };
  // Search tags (saved searches) state
  interface SearchTag {
    id: number;
    collection_id: number | null;
    name: string;
    search_string: string;
    description: string | null;
    created_at: string | null;
    updated_at: string | null;
  }
  const [searchTags, setSearchTags] = React.useState<SearchTag[]>([]);
  const [showTagDropdown, setShowTagDropdown] = React.useState(false);
  const [activeTagId, setActiveTagId] = React.useState<number | null>(null);
  const [editingTagId, setEditingTagId] = React.useState<number | null>(null);
  const [editingTagName, setEditingTagName] = React.useState('');
  const [editingTagSearch, setEditingTagSearch] = React.useState('');
  const [editingTagDescription, setEditingTagDescription] = React.useState('');
  const [savingTag, setSavingTag] = React.useState(false);
  const [showSaveTagPrompt, setShowSaveTagPrompt] = React.useState(false);
  const [newTagName, setNewTagName] = React.useState('');
  
  // Resolve loose→exact dialog state
  const [resolveDialogTerms, setResolveDialogTerms] = React.useState<ResolvedTerm[] | null>(null);
  const [pendingSaveAction, setPendingSaveAction] = React.useState<
    ((resolvedSearchString: string) => Promise<void>) | null
  >(null);
  
  // View-only sharees can see bindles but not create/edit/delete
  const canEditBindles = collectionPermission !== 'view';

  // Fetch search tags for current collection
  const fetchSearchTags = React.useCallback(async () => {
    if (!token) return;
    
    // For pseudo-collections other than "All Items", bindles are not applicable
    // unless a specific collectionId is provided (real collection).
    if (!collectionId && collection !== 'all') {
      setSearchTags([]);
      return;
    }
    
    try {
      const url = collectionId
        ? API_ENDPOINTS.GET_BINDLES(collectionId)
        : API_ENDPOINTS.GET_BINDLES();
      const resp = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setSearchTags(data.tags || []);
      }
    } catch (err) {
      console.error('Failed to fetch search tags:', err);
    }
  }, [token, collectionId, collection]);

  React.useEffect(() => {
    fetchSearchTags();
  }, [fetchSearchTags]);

  // Listen for bindle updates (e.g. recipe scan auto-creates a bindle)
  React.useEffect(() => {
    const handler = () => fetchSearchTags();
    window.addEventListener('bindles-updated', handler);
    return () => window.removeEventListener('bindles-updated', handler);
  }, [fetchSearchTags]);

  // Reset saved search state when collection changes
  React.useEffect(() => {
    setActiveTagId(null);
    setEditingTagId(null);
    setShowSaveTagPrompt(false);
    setShowTagDropdown(false);
    setNewTagName('');
  }, [collectionId]);

  // Restore active tag after bindles are fetched (e.g. after tab switch)
  // Match current searchTerm against bindle search_string to recover activeTagId
  React.useEffect(() => {
    if (!searchTerm || activeTagId || searchTags.length === 0) return;
    const trimmed = searchTerm.trim();
    const match = searchTags.find(t => t.search_string === trimmed);
    if (match) {
      setActiveTagId(match.id);
    }
  }, [searchTags]); // Only run when searchTags are loaded/changed

  // Clear active tag when search term is cleared (e.g. via Clear Filter)
  React.useEffect(() => {
    if (!searchTerm) {
      setActiveTagId(null);
    }
  }, [searchTerm]);

  // Reset editing state when bottom sheet closes
  React.useEffect(() => {
    if (!showTagDropdown) {
      setEditingTagId(null);
      setShowSaveTagPrompt(false);
    }
  }, [showTagDropdown]);

  // Check for loose terms and show resolve dialog, or proceed directly
  const resolveAndSave = (
    searchStr: string,
    saveAction: (resolvedSearchString: string) => Promise<void>
  ) => {
    const itemNames = items.map(i => i.name);
    const looseTerms = resolveLooseTerms(searchStr, itemNames);

    if (looseTerms.length > 0) {
      // Show dialog — store the save callback for later
      setResolveDialogTerms(looseTerms);
      setPendingSaveAction(() => saveAction);
    } else {
      // No loose terms to resolve — save directly
      saveAction(searchStr);
    }
  };

  const handleResolveConfirm = (conversions: Map<string, string | null>) => {
    if (!pendingSaveAction || !resolveDialogTerms) return;
    const currentSearch = editingTagId !== null ? editingTagSearch.trim() : searchTerm.trim();
    const resolvedSearch = applyTermConversions(currentSearch, conversions);

    // Update the search bar to show the resolved terms
    if (editingTagId === null) {
      onSearchChange(resolvedSearch);
    }

    pendingSaveAction(resolvedSearch);
    setResolveDialogTerms(null);
    setPendingSaveAction(null);
  };

  const handleResolveCancel = () => {
    setResolveDialogTerms(null);
    setPendingSaveAction(null);
  };

  const handleSaveTag = async () => {
    if (!token || !newTagName.trim() || !searchTerm.trim()) return;

    resolveAndSave(searchTerm.trim(), async (resolvedSearch) => {
      setSavingTag(true);
      try {
        const resp = await fetch(API_ENDPOINTS.CREATE_BINDLE, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: newTagName.trim(),
            search_string: resolvedSearch,
            collection_id: collectionId || null,
          }),
        });
        if (resp.ok) {
          const tag = await resp.json();
          setSearchTags(prev => [...prev, tag].sort((a, b) => a.name.localeCompare(b.name)));
          setActiveTagId(tag.id);
          setShowSaveTagPrompt(false);
          setNewTagName('');
          setShowTagDropdown(false);
        } else {
          const errData = await resp.json().catch(() => ({}));
          console.error('Failed to save search tag:', resp.status, errData);
        }
      } catch (err) {
        console.error('Failed to save search tag:', err);
      } finally {
        setSavingTag(false);
      }
    });
  };

  const handleUpdateTag = async (tagId: number) => {
    if (!token) return;
    const updates: any = {};
    if (editingTagName.trim()) updates.name = editingTagName.trim();
    if (editingTagSearch.trim()) updates.search_string = editingTagSearch.trim();
    updates.description = editingTagDescription.trim();
    if (Object.keys(updates).length === 0) return;

    // Only resolve if search_string is being updated
    if (updates.search_string) {
      resolveAndSave(updates.search_string, async (resolvedSearch) => {
        updates.search_string = resolvedSearch;
        try {
          const resp = await fetch(API_ENDPOINTS.UPDATE_BINDLE(tagId), {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(updates),
          });
          if (resp.ok) {
            const updated = await resp.json();
            setSearchTags(prev => prev.map(t => t.id === tagId ? updated : t).sort((a, b) => a.name.localeCompare(b.name)));
            setEditingTagId(null);
          }
        } catch (err) {
          console.error('Failed to update search tag:', err);
        }
      });
    } else {
      // No search_string change — save directly (name/description only)
      try {
        const resp = await fetch(API_ENDPOINTS.UPDATE_BINDLE(tagId), {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(updates),
        });
        if (resp.ok) {
          const updated = await resp.json();
          setSearchTags(prev => prev.map(t => t.id === tagId ? updated : t).sort((a, b) => a.name.localeCompare(b.name)));
          setEditingTagId(null);
        }
      } catch (err) {
        console.error('Failed to update search tag:', err);
      }
    }
  };

  const handleDeleteTag = async (tagId: number) => {
    if (!token) return;
    try {
      const resp = await fetch(API_ENDPOINTS.DELETE_BINDLE(tagId), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        setSearchTags(prev => prev.filter(t => t.id !== tagId));
        if (activeTagId === tagId) setActiveTagId(null);
      }
    } catch (err) {
      console.error('Failed to delete search tag:', err);
    }
  };

  const handleSelectTag = (tag: SearchTag) => {
    onSearchChange(tag.search_string);
    setActiveTagId(tag.id);
    setShowTagDropdown(false);
  };

  // Detect if active tag's search string has been modified
  const activeTag = activeTagId ? searchTags.find(t => t.id === activeTagId) : null;
  const isActiveTagModified = activeTag && searchTerm.trim() && activeTag.search_string !== searchTerm.trim();

  // Notify parent of active bindle changes (use ref to avoid infinite loop from inline callback)
  const onActiveBindleChangeRef = React.useRef(onActiveBindleChange);
  onActiveBindleChangeRef.current = onActiveBindleChange;
  const activeTagName = activeTag?.name;
  const activeTagDesc = activeTag?.description;
  const activeTagSearchStr = activeTag?.search_string;
  React.useEffect(() => {
    if (activeTagId && activeTagName != null) {
      onActiveBindleChangeRef.current?.({ name: activeTagName, description: activeTagDesc ?? null, search_string: activeTagSearchStr ?? '' });
    } else {
      onActiveBindleChangeRef.current?.(null);
    }
  }, [activeTagId, activeTagName, activeTagDesc, activeTagSearchStr]);

  const handleOverwriteTag = async () => {
    if (!token || !activeTag || !searchTerm.trim()) return;

    resolveAndSave(searchTerm.trim(), async (resolvedSearch) => {
      setSavingTag(true);
      try {
        const resp = await fetch(API_ENDPOINTS.UPDATE_BINDLE(activeTag.id), {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ search_string: resolvedSearch }),
        });
        if (resp.ok) {
          const updated = await resp.json();
          setSearchTags(prev => prev.map(t => t.id === activeTag.id ? updated : t));
          setShowSaveTagPrompt(false);
          setShowTagDropdown(false);
        }
      } catch (err) {
        console.error('Failed to overwrite search tag:', err);
      } finally {
        setSavingTag(false);
      }
    });
  };

  const [menuPosition, setMenuPosition] = React.useState({ top: 0, right: 0 });
  const manageMenuRef = React.useRef<HTMLDivElement>(null);
  const manageButtonRef = React.useRef<HTMLButtonElement>(null);
  
  // Use refs for values that need to be current in voice recognition callback
  const isChecklistModeRef = React.useRef(isChecklistMode);
  const itemsRef = React.useRef(items);
  const onToggleItemRef = React.useRef(onToggleItem);
  
  // Update refs when props change
  React.useEffect(() => {
    isChecklistModeRef.current = isChecklistMode;
    itemsRef.current = items;
    onToggleItemRef.current = onToggleItem;
  }, [isChecklistMode, items, onToggleItem]);
  
  // Smart retry: If voice search returns 0 results, try variations
  React.useEffect(() => {
    // Clear any pending retry
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    // Track when search term changes from user input (not from retry)
    const searchLower = searchTerm.toLowerCase().trim();
    
    // Clear history only when voice listening stops (new search session)
    if (!isListening && retriedTermsRef.current.size > 0) {
      retriedTermsRef.current.clear();
    }
    
    // If we found results, clear the retry history to stop retrying
    if (filteredItemsCount > 0 && retriedTermsRef.current.size > 0) {
      retriedTermsRef.current.clear();
    }
    
    lastSearchTermRef.current = searchLower;
    
    // Only retry if:
    // 1. We're listening (voice search active)
    // 2. There's a search term
    // 3. No results found
    // 4. Haven't already tried this exact term
    // 5. Haven't tried too many times (max 5 attempts to allow chaining)
    if (isListening && searchTerm && filteredItemsCount === 0 && 
        !retriedTermsRef.current.has(searchLower) && 
        retriedTermsRef.current.size < 5) {
      // Mark this term as tried
      retriedTermsRef.current.add(searchLower);
      
      const words = searchLower.split(/\s+/);
      let retryTerm: string | null = null;
      
      // Strategy 1: If multiple words, try first word only (handles "banana ok", "chocolate bar", etc.)
      if (words.length > 1) {
        const firstWord = words[0];
        // If first word already tried, try its variations
        if (retriedTermsRef.current.has(firstWord)) {
          const variations = normalizeTerm(firstWord);
          retryTerm = variations.find(v => v !== firstWord && !retriedTermsRef.current.has(v)) || null;
        } else {
          retryTerm = firstWord;
        }
      }
      // Strategy 2: Try plural/singular variations for single words
      else if (words.length === 1) {
        const variations = normalizeTerm(searchLower);
        // Try the first variation that's different and not already tried
        retryTerm = variations.find(v => v !== searchLower && !retriedTermsRef.current.has(v)) || null;
      }
      
      // Only retry if we found a new term to try
      if (retryTerm && !retriedTermsRef.current.has(retryTerm)) {
        const termToTry = retryTerm; // Capture for closure
        // Small delay to avoid rapid-fire retries
        retryTimeoutRef.current = setTimeout(() => {
          onSearchChange(termToTry);
        }, 300);
      }
    }
    
    // Cleanup on unmount
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [isListening, searchTerm, filteredItemsCount, onSearchChange]);

  // Calculate menu position
  React.useEffect(() => {
    if (showManageMenu && manageButtonRef.current) {
      const rect = manageButtonRef.current.getBoundingClientRect();
      const pos = {
        top: rect.bottom + 2,
        right: window.innerWidth - rect.right
      };
      setMenuPosition(pos);
    }
  }, [showManageMenu]);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (manageMenuRef.current && !manageMenuRef.current.contains(event.target as Node) &&
          manageButtonRef.current && !manageButtonRef.current.contains(event.target as Node)) {
        setShowManageMenu(false);
      }
    };

    if (showManageMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showManageMenu]);

  const isSelectWithSelection = isSelectMode && selectedCount > 0;
  const [showCollectionModal, setShowCollectionModal] = React.useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = React.useState<number | undefined>(undefined);
  const [selectedCollectionName, setSelectedCollectionName] = React.useState<string>('');
  const [collectionRefreshTrigger, setCollectionRefreshTrigger] = React.useState(0);
  const [sharingCollectionId, setSharingCollectionId] = React.useState<number | null>(null);

  // Setup voice recognition with continuous listening
  React.useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      // Helper: split transcript words into comma-separated, deduplicated search terms
      const wordsToSearchTerms = (transcript: string): string => {
        const words = transcript.trim().toLowerCase().split(/\s+/).filter(Boolean);
        // Deduplicate while preserving order
        const seen = new Set<string>();
        const unique: string[] = [];
        for (const w of words) {
          if (!seen.has(w)) {
            seen.add(w);
            unique.push(w);
          }
        }
        return unique.join(', ');
      };
      
      // Stop words to filter out of search terms
      const stopWords = new Set(['a', 'an', 'the', 'and', 'or', 'of', 'to', 'in', 'for', 'is', 'it', 'that', 'this', 'with']);

      recognitionRef.current.onresult = (event: any) => {
        // Build full transcript from ALL results in the event
        // Add space between each result to prevent word concatenation
        let fullTranscript = '';
        let allFinal = true;
        for (let i = 0; i < event.results.length; i++) {
          if (i > 0) fullTranscript += ' ';
          fullTranscript += event.results[i][0].transcript;
          if (!event.results[i].isFinal) allFinal = false;
        }
        
        const fullLower = fullTranscript.toLowerCase().trim();
        
        // Skip if empty
        if (!fullLower) return;
        
        // Get just the latest result for command detection
        const lastResultIndex = event.results.length - 1;
        const latestTranscript = event.results[lastResultIndex][0].transcript;
        const isFinal = event.results[lastResultIndex].isFinal;
        const latestLower = latestTranscript.toLowerCase().trim();
        
        // Command detection on the latest result
        const isOkCommand = latestLower === 'ok' || latestLower === 'okay' || latestLower === 'done';
        const isNoCommand = latestLower === 'no' || latestLower === 'cancel';
        const trailingStopWordMatch = fullLower.match(/^(.+?)\s+(ok|okay|done)\s*$/);
        const hasTrailingStopWord = !!trailingStopWordMatch;
        
        // Checklist commands
        const isGotEverything = (
          latestLower === 'got everything' || 
          latestLower === 'got it all' || 
          latestLower === 'everything' ||
          latestLower === 'got all'
        ) && isChecklistModeRef.current;
        
        const isClearList = (
          latestLower === 'clear list' || 
          latestLower === 'clear the list' || 
          latestLower === 'clear checklist' ||
          latestLower === 'clear all' ||
          latestLower === 'reset list' ||
          latestLower === 'reset the list' ||
          latestLower === 'reset checklist' ||
          latestLower === 'reset all' ||
          latestLower === 'reset'
        ) && isChecklistModeRef.current;
        
        const gotMatch = latestLower.match(/^got\s+(.+)$/);
        const startsWithGot = latestLower.startsWith('got');
        const isGotCommand = startsWithGot && isChecklistModeRef.current && !isGotEverything && !isClearList;
        
        // Debug logging for got commands
        if (isFinal && startsWithGot && isChecklistModeRef.current) {
          console.log('[GOT] Transcript:', latestLower);
          console.log('[GOT] isGotEverything:', isGotEverything);
          console.log('[GOT] isClearList:', isClearList);
          console.log('[GOT] isGotCommand:', isGotCommand);
          console.log('[GOT] gotMatch:', gotMatch);
        }
        
        // Handle commands on final results
        if (isFinal) {
          if (isOkCommand) {
            onSearchChange(lastTranscriptRef.current);
            if (recognitionRef.current) recognitionRef.current.stop();
            return;
          }
          
          if (hasTrailingStopWord && trailingStopWordMatch) {
            // "banana cheese bread ok" → keep everything before "ok"
            const cleanWords = trailingStopWordMatch[1].trim();
            const search = wordsToSearchTerms(cleanWords);
            onSearchChange(search);
            lastTranscriptRef.current = search;
            if (recognitionRef.current) recognitionRef.current.stop();
            return;
          }
          
          if (isNoCommand) {
            onSearchChange('');
            if (recognitionRef.current) recognitionRef.current.stop();
            return;
          }
          
          if (isGotEverything && onCheckAll) {
            onCheckAll();
            onSearchChange('');
            if (recognitionRef.current) recognitionRef.current.stop();
            return;
          }
          
          if (isClearList && onUncheckAll) {
            onUncheckAll();
            onSearchChange('');
            if (recognitionRef.current) recognitionRef.current.stop();
            return;
          }
          
          if (isGotCommand && gotMatch && onToggleItemRef.current) {
            const itemsText = gotMatch[1].trim();
            const words = itemsText.toLowerCase().split(/\s+/);
            const matchedItems = new Set<number>();
            
            let i = 0;
            while (i < words.length) {
              let bestMatch: { item: any; len: number } | null = null;
              for (let len = Math.min(3, words.length - i); len >= 1; len--) {
                const phrase = words.slice(i, i + len).join(' ');
                const matchedItem = itemsRef.current.find(item => {
                  if (matchedItems.has(item.id)) return false;
                  const itemNameLower = item.name.toLowerCase();
                  return itemNameLower.includes(phrase) || phrase.includes(itemNameLower);
                });
                if (matchedItem) {
                  if (!bestMatch || len > bestMatch.len) {
                    bestMatch = { item: matchedItem, len };
                  }
                }
              }
              if (bestMatch) {
                matchedItems.add(bestMatch.item.id);
                if (onToggleItemRef.current) onToggleItemRef.current(bestMatch.item.id);
                i += 1;
              } else {
                i++;
              }
            }
            
            onSearchChange('');
            if (recognitionRef.current) recognitionRef.current.stop();
            return;
          }
        }
        
        // Don't show command keywords in interim results
        if (isOkCommand || isNoCommand || isGotCommand || isGotEverything || isClearList || hasTrailingStopWord) {
          return;
        }
        
        // Split transcript words into comma-separated search terms
        // Filter out common stop words for cleaner search
        const allWords = fullTranscript.trim().toLowerCase().split(/\s+/).filter(Boolean);
        const searchWords = allWords.filter(w => !stopWords.has(w));
        
        // Greedy adjacent-pair merging: if two adjacent words together match
        // an item name, keep them as one term (e.g. "green onion" stays together)
        const itemNames = itemsRef.current.map(item => item.name.toLowerCase());
        const merged: string[] = [];
        let i = 0;
        while (i < searchWords.length) {
          if (i + 1 < searchWords.length) {
            const pair = searchWords[i] + ' ' + searchWords[i + 1];
            // Check if the pair matches any item name (substring match)
            if (itemNames.some(name => name.includes(pair))) {
              merged.push(pair);
              i += 2;
              continue;
            }
          }
          merged.push(searchWords[i]);
          i++;
        }
        
        // Deduplicate while preserving order
        const seen = new Set<string>();
        const unique: string[] = [];
        for (const w of merged) {
          if (!seen.has(w)) {
            seen.add(w);
            unique.push(w);
          }
        }
        const searchTerms = unique.join(', ');
        
        onSearchChange(searchTerms);
        if (isFinal) {
          lastTranscriptRef.current = searchTerms;
          lastSearchTermRef.current = searchTerms;
          lastSpeechTimeRef.current = Date.now();
        }
      };

      recognitionRef.current.onstart = () => {
        setIsListening(true);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        lastTranscriptRef.current = '';
        lastSpeechTimeRef.current = 0;
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error('Voice recognition error:', event.error);
        setIsListening(false);
        
        if (event.error === 'not-allowed') {
          console.warn('Microphone permission denied');
        } else if (event.error === 'no-speech') {
          console.warn('No speech detected');
        }
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [onSearchChange]);

  // Keep ref in sync so the toggle function always has current state
  React.useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  // Stable voice toggle function (uses refs, no stale closures)
  const toggleVoice = React.useCallback(() => {
    if (recognitionRef.current) {
      if (isListeningRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          console.error('Failed to stop voice recognition:', error);
          setIsListening(false);
        }
      } else {
        try {
          lastSpeechTimeRef.current = 0;
          lastTranscriptRef.current = '';
          recognitionRef.current.start();
        } catch (error) {
          console.error('Failed to start voice recognition:', error);
          setIsListening(false);
        }
      }
    }
  }, []);

  // Register voice toggle with parent for direct invocation (preserves iOS user gesture)
  React.useEffect(() => {
    onRegisterVoiceToggle?.(toggleVoice);
  }, [toggleVoice, onRegisterVoiceToggle]);

  // Listen for voice search trigger from sock icon (fallback for custom event)
  React.useEffect(() => {
    window.addEventListener('startVoiceSearch', toggleVoice);
    return () => window.removeEventListener('startVoiceSearch', toggleVoice);
  }, [toggleVoice]);

  // Show collection modal (for create/edit)
  const handleShowCollectionModal = (id?: number, name?: string) => {
    setSelectedCollectionId(id);
    setSelectedCollectionName(name || '');
    setShowCollectionModal(true);
  };

  // Empty collection handler
  const handleEmptyCollection = async (id: number, name: string) => {
    if (!window.confirm(`Remove all items from "${name}"? The items will not be deleted, only removed from this collection.`)) {
      return;
    }
    
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/collections/${id}/items`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setCollectionRefreshTrigger(prev => prev + 1);
        onCollectionsRefresh?.();
        window.dispatchEvent(new CustomEvent('collections-updated'));
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to empty collection');
      }
    } catch (error) {
      console.error('Error emptying collection:', error);
      alert('Failed to empty collection');
    }
  };

  // Delete collection handler
  const handleDeleteCollection = async (id: number, name: string) => {
    if (!window.confirm(`Delete collection "${name}"? This will not delete the items.`)) {
      return;
    }
    
    if (!token) return;

    try {
      const response = await fetch(API_ENDPOINTS.DELETE_COLLECTION(id), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setCollectionRefreshTrigger(prev => prev + 1);
        onCollectionsRefresh?.();
        window.dispatchEvent(new CustomEvent('collections-updated'));
        // If we're viewing the deleted collection, switch to "all"
        if (collection === id.toString()) {
          onCollectionChange('all');
        }
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete collection');
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
      alert('Failed to delete collection');
    }
  };

  return (
    <div className="unified-inventory-menu-bar transparent-menu-bar">
      {/* Search Input */}
      <div className={`menu-search-container ${isSearchFocused || isListening ? 'focused' : ''}`} style={{ marginLeft: '0' }}>
        <button
          className="menu-search-icon-btn"
          onClick={() => {
            // Search icon is just decorative, no action
          }}
          disabled={isSelectWithSelection}
          title="Search"
          aria-label="Search"
        >
          <svg className={`menu-search-icon ${isListening ? 'listening' : ''}`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="M21 21l-4.35-4.35"></path>
          </svg>
        </button>
        <input
          type="text"
          className={`menu-search-input${activeTag && !isSearchFocused ? ' bindle-active' : ''}`}
          placeholder={isSearchFocused ? "name, #category, @location, ~description" : "name, #cat, @loc, ~desc"}
          value={searchTerm}
          onChange={(e) => {
            // Stop listening when user starts typing manually
            if (isListening && recognitionRef.current) {
              recognitionRef.current.stop();
            }
            onSearchChange(e.target.value);
          }}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          disabled={isSelectWithSelection}
          style={activeTag && !isSearchFocused ? { color: 'transparent' } : undefined}
        />
        {/* Show saved search name when not focused */}
        {activeTag && !isSearchFocused && (
          <div className="menu-search-tag-label">
            {activeTag.name}
          </div>
        )}
        {/* Right-side buttons inside search bar */}
        <div className="menu-search-actions">
          {searchTerm && (
            <>
              {/* AND/OR toggle — visible when search has 2+ terms and input is focused */}
              {isSearchFocused && (searchTerm.includes(',') || searchTerm.includes('+')) && (
              <button
                className="menu-search-action-btn search-bool-toggle"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const isAnd = hasAndOperator(searchTerm);
                  onSearchChange(toggleSearchOperator(searchTerm, !isAnd));
                }}
                onMouseDown={(e) => e.preventDefault()}
                onTouchEnd={(e) => e.stopPropagation()}
                title={hasAndOperator(searchTerm) ? 'Switch to OR (match any)' : 'Switch to AND (match all)'}
                aria-label={hasAndOperator(searchTerm) ? 'Switch to OR' : 'Switch to AND'}
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  padding: '2px 6px',
                  borderRadius: '4px',
                  background: hasAndOperator(searchTerm) ? 'var(--color-accent)' : 'var(--color-bg-tertiary)',
                  color: hasAndOperator(searchTerm) ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                  border: hasAndOperator(searchTerm) ? 'none' : '1px solid var(--color-border)',
                  letterSpacing: '0.5px',
                  lineHeight: '1.4',
                  minWidth: 'unset',
                  whiteSpace: 'nowrap',
                }}
              >
                {hasAndOperator(searchTerm) ? 'AND' : 'OR'}
              </button>
              )}
              {/* Save search as tag — only visible when search input is focused */}
              {canEditBindles && isSearchFocused && (
              <button
                className={`menu-search-action-btn${activeTag && !isActiveTagModified ? ' bookmark-saved' : ' bookmark-unsaved'}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  (document.activeElement as HTMLElement)?.blur();
                  setShowSaveTagPrompt(true);
                  setShowTagDropdown(true);
                  setNewTagName(activeTag ? activeTag.name : '');
                }}
                onMouseDown={(e) => e.preventDefault()}
                onTouchEnd={(e) => e.stopPropagation()}
                title={activeTag && !isActiveTagModified ? 'Search saved' : 'Save search'}
                aria-label={activeTag && !isActiveTagModified ? 'Search saved' : 'Save search'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill={activeTag && !isActiveTagModified ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"></path>
                </svg>
              </button>
              )}
            </>
          )}
          {/* Tag dropdown toggle */}
          <button
            className={`menu-search-action-btn tag-toggle ${showTagDropdown ? 'active' : ''} ${searchTags.length > 0 || showSaveTagPrompt ? '' : 'hidden-no-tags'}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              (document.activeElement as HTMLElement)?.blur();
              setShowTagDropdown(prev => !prev);
              setEditingTagId(null);
              setShowSaveTagPrompt(false);
            }}
            onMouseDown={(e) => e.preventDefault()}
            onTouchEnd={(e) => e.stopPropagation()}
            title="Bindles"
            aria-label="Bindles"
            style={{ display: (searchTags.length > 0 || searchTerm) ? 'flex' : 'none' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: showTagDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
        </div>

      </div>

      {/* Saved Searches Modal — single centered modal */}
      {(showTagDropdown || showSaveTagPrompt || editingTagId !== null) && createPortal(
        <div className="search-tag-modal-overlay" onClick={() => {
          setShowTagDropdown(false);
          setShowSaveTagPrompt(false);
          setEditingTagId(null);
        }}>
          <div className="search-tag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-tag-modal-handle" />
            {/* Header — hidden when editing (Save/Cancel close the modal) */}
            {editingTagId === null && (
              <div className="search-tag-modal-header">
                <div className="search-tag-modal-title">Bindles</div>
                <ModalCloseButton
                  onClick={() => {
                    setShowTagDropdown(false);
                    setShowSaveTagPrompt(false);
                    setEditingTagId(null);
                  }}
                  style={{ position: 'absolute', right: 16, top: 4 }}
                />
              </div>
            )}

            {/* Edit form at top */}
            {editingTagId !== null && (() => {
              const editTag = searchTags.find(t => t.id === editingTagId);
              return editTag ? (
                <div className="search-tag-form-section search-tag-form-editing" style={{ position: 'relative' }}>
                  <ModalCloseButton
                    onClick={() => setEditingTagId(null)}
                    style={{ position: 'absolute', right: 16, top: 0, zIndex: 1 }}
                  />
                  <label className="search-tag-field-label">Name</label>
                  <input
                    type="text"
                    className="search-tag-input"
                    value={editingTagName}
                    onChange={(e) => setEditingTagName(e.target.value)}
                    enterKeyHint="done"
                    autoFocus
                  />
                  <label className="search-tag-field-label">Search terms</label>
                  <textarea
                    className="search-tag-input search-tag-input-multiline"
                    rows={2}
                    value={editingTagSearch}
                    onChange={(e) => setEditingTagSearch(e.target.value)}
                  />
                  <label className="search-tag-field-label">Description</label>
                  <textarea
                    className="search-tag-textarea search-tag-textarea-expand"
                    value={editingTagDescription}
                    onChange={(e) => setEditingTagDescription(e.target.value)}
                    placeholder="Optional — e.g., recipe instructions, notes..."
                  />
                  <div className="search-tag-form-actions">
                    <button className="search-tag-btn primary" onClick={() => handleUpdateTag(editTag.id)}>Save</button>
                    <button className="search-tag-btn" onClick={() => setEditingTagId(null)}>Cancel</button>
                    <button
                      className="search-tag-btn search-tag-delete-btn"
                      onClick={() => {
                        if (window.confirm(`Delete "${editTag.name}"?`)) {
                          handleDeleteTag(editTag.id);
                          setEditingTagId(null);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Save/update form at top */}
            {canEditBindles && showSaveTagPrompt && searchTerm && editingTagId === null && (
              <div className="search-tag-form-section">
                <div className="search-tag-form-label">Search: <strong>{searchTerm}</strong></div>
                <div className="search-tag-save-row">
                  <input
                    type="text"
                    className="search-tag-input"
                    placeholder="Name this search..."
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (activeTag && newTagName.trim() === activeTag.name) {
                          handleOverwriteTag();
                        } else {
                          handleSaveTag();
                        }
                      }
                    }}
                    enterKeyHint="done"
                    autoFocus
                  />
                  <button
                    className="search-tag-save-btn"
                    onClick={() => {
                      if (activeTag && newTagName.trim() === activeTag.name) {
                        handleOverwriteTag();
                      } else {
                        handleSaveTag();
                      }
                    }}
                    disabled={!newTagName.trim() || savingTag}
                  >
                    {savingTag ? '...' : (activeTag && newTagName.trim() === activeTag.name ? 'Update' : 'Save')}
                  </button>
                </div>
              </div>
            )}

            {/* Scrollable tag list — hidden when editing */}
            {editingTagId === null && <div className="search-tag-modal-list">
              {searchTags.length === 0 ? (
                <div className="search-tag-empty">No bindles yet. Type a search and tap the bookmark icon to create one.</div>
              ) : (
                searchTags.map(tag => (
                  <div key={tag.id} className={`search-tag-item ${activeTagId === tag.id ? 'active' : ''}`}>
                    <div className="search-tag-row">
                      <button
                        className="search-tag-select"
                        onClick={() => handleSelectTag(tag)}
                      >
                        <span className="search-tag-name">{tag.name}</span>
                        <span className="search-tag-terms">{tag.search_string}</span>
                        {tag.description && (
                          <span className="search-tag-description">{tag.description}</span>
                        )}
                      </button>
                      {canEditBindles && (
                      <div className="search-tag-actions">
                        <button
                          className="search-tag-action-btn"
                          onClick={() => {
                            setEditingTagId(tag.id);
                            setEditingTagName(tag.name);
                            setEditingTagSearch(tag.search_string);
                            setEditingTagDescription(tag.description || '');
                            setShowSaveTagPrompt(false);
                          }}
                          title="Edit"
                        >
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="3"></circle>
                            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"></path>
                          </svg>
                        </button>
                      </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>}
          </div>
        </div>,
        document.body
      )}

      {/* Filter Button */}
      <button 
        className="menu-icon-btn filter-btn"
        onClick={onFilterClick}
        title={isSelectWithSelection ? "Disabled while items are selected" : "Filter Options"}
        disabled={isSelectWithSelection}
        aria-label="Filter options"
        style={{ position: 'relative', marginLeft: '8px' }}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
        </svg>
        {hasActiveFilters && (
          <span style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            width: '8px',
            height: '8px',
            background: 'var(--color-accent-secondary)',
            borderRadius: '50%',
            border: '2px solid white'
          }}></span>
        )}
      </button>

      {/* Right: Display & Manage (3-dot at end) */}
      <div className="unified-right-actions" style={{ gap: '8px', marginLeft: 'auto' }}>
        {/* Sort Button */}
        <button 
          className="menu-icon-btn display-btn"
          onClick={onDisplayClick}
          title={isSelectWithSelection ? "Disabled while items are selected" : "Sort Options"}
          disabled={isSelectWithSelection}
          aria-label="Sort options"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="4" y1="6" x2="20" y2="6"></line>
            <line x1="4" y1="12" x2="16" y2="12"></line>
            <line x1="4" y1="18" x2="12" y2="18"></line>
          </svg>
        </button>

        {/* 3-dot Menu Button (always shows menu) */}
        <button
          ref={manageButtonRef}
          className={`menu-icon-btn manage-btn ${isSelectMode ? 'active' : ''}`}
          onClick={() => setShowManageMenu(!showManageMenu)}
          aria-label="More options"
          aria-expanded={showManageMenu}
          style={{ position: 'relative' }}
        >
          {/* Three Dots (Vertical) */}
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="5" r="1.5" fill="currentColor" stroke="none"></circle>
            <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"></circle>
            <circle cx="12" cy="19" r="1.5" fill="currentColor" stroke="none"></circle>
          </svg>
          {isSelectMode && (
            <span className="manage-select-check" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="4 13 9 18 20 7" />
              </svg>
            </span>
          )}
          
          {selectedCount > 0 && (
            <span className="menu-badge" style={{ position: 'absolute', top: '2px', right: '2px' }}>{selectedCount}</span>
          )}
        </button>
      </div>

      {/* 3-dot Dropdown Menu */}
      {showManageMenu && createPortal(
        <div 
          ref={manageMenuRef}
          className="manage-dropdown" 
          style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
        >
          {/* Section 1: Select Mode Toggle */}
          {!isSelectMode ? (
            <button 
              className="dropdown-item" 
              onClick={() => { 
                onSelectModeToggle(); 
                setShowManageMenu(false); 
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11l3 3L22 4"></path>
                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
              </svg>
              Select Mode
            </button>
          ) : (
            <>
              <button 
                className="dropdown-item" 
                onClick={() => { 
                  onSelectModeToggle(); 
                  setShowManageMenu(false); 
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Exit Select Mode
              </button>
              <button 
                className="dropdown-item" 
                onClick={() => { 
                  onClearSelection?.(); 
                  setShowManageMenu(false); 
                }} 
                disabled={selectedCount === 0}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                </svg>
                Clear Selection
              </button>
              <button 
                className="dropdown-item" 
                onClick={() => { 
                  onSelectAll?.(); 
                  setShowManageMenu(false); 
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 11l3 3L22 4"></path>
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
                </svg>
                Select All
              </button>
            </>
          )}

          {/* Section 2: Checklist Actions (only in checklist mode, but not for shopping lists) */}
          {isChecklistMode && !isStockKeeping && (
            <>
              <div className="dropdown-divider"></div>
              <button 
                className="dropdown-item" 
                onClick={() => { 
                  onCheckAll?.(); 
                  setShowManageMenu(false); 
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 11 12 14 22 4"></polyline>
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
                </svg>
                Check All
              </button>
              {items.some(item => item.checked || (item as any).is_checked) && (
                <button 
                  className="dropdown-item" 
                  onClick={() => { 
                    onUncheckAll?.(); 
                    setShowManageMenu(false); 
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  </svg>
                  Clear All Checks
                </button>
              )}
            </>
          )}

          {/* Share Grocery — only shown when viewing Grocery collection */}
          {kitchenCollectionId && !isSelectMode && (
            <>
              <div className="dropdown-divider"></div>
              <button
                className="dropdown-item"
                onClick={() => { setSharingCollectionId(kitchenCollectionId); setShowManageMenu(false); }}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                Share Grocery
              </button>
            </>
          )}

          {/* Section 3: Actions on Selected Items (only when items selected) */}
          {isSelectMode && selectedCount > 0 && (
            <>
              <div className="dropdown-divider"></div>
              <>
                  <button 
                    className="dropdown-item" 
                    onClick={() => { 
                      onAction?.('share', selectedItems); 
                      setShowManageMenu(false); 
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="18" cy="5" r="3"></circle>
                      <circle cx="6" cy="12" r="3"></circle>
                      <circle cx="18" cy="19" r="3"></circle>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                    Share Selected
                  </button>
                  <button 
                    className="dropdown-item" 
                    onClick={() => { 
                      onAction?.('bulk-edit', selectedItems); 
                      setShowManageMenu(false); 
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                    Edit Selected
                  </button>
                  <button 
                    className="dropdown-item" 
                    onClick={() => { 
                      onAction?.('assign-qr', selectedItems); 
                      setShowManageMenu(false); 
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                    Assign to QR Code
                  </button>
                  
                  <div className="dropdown-divider"></div>
                  <button
                    className="dropdown-item"
                    onClick={() => setShowCollectionSubmenu(prev => !prev)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"></path>
                    </svg>
                    Collection...
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{ marginLeft: 'auto', transform: showCollectionSubmenu ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>

                  {showCollectionSubmenu && (
                    <div style={{ 
                      marginLeft: '24px', 
                      borderLeft: '2px solid var(--color-border)',
                      paddingLeft: '8px',
                    }}>
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          onAction?.('add-to-collection', selectedItems);
                          setShowManageMenu(false);
                          setShowCollectionSubmenu(false);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"></path>
                          <line x1="12" y1="11" x2="12" y2="17"></line>
                          <line x1="9" y1="14" x2="15" y2="14"></line>
                        </svg>
                        Add to...
                      </button>

                      {canMoveRemoveFromCurrentCollection && (
                        <>
                          <button
                            className="dropdown-item"
                            onClick={() => {
                              onAction?.('move-to-collection', selectedItems);
                              setShowManageMenu(false);
                              setShowCollectionSubmenu(false);
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"></path>
                              <polyline points="12 8 16 12 12 16"></polyline>
                              <line x1="8" y1="12" x2="16" y2="12"></line>
                            </svg>
                            Move to...
                          </button>

                          <button
                            className="dropdown-item danger"
                            onClick={() => {
                              onAction?.('remove-from-collection', selectedItems);
                              setShowManageMenu(false);
                              setShowCollectionSubmenu(false);
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"></path>
                              <line x1="9" y1="14" x2="15" y2="14"></line>
                            </svg>
                            Remove from here
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  
                  <div className="dropdown-divider"></div>
                  <button 
                    className="dropdown-item danger" 
                    onClick={() => { 
                      onAction?.('delete', selectedItems); 
                      setShowManageMenu(false); 
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
                    </svg>
                    Delete Selected
                  </button>
                </>
            </>
          )}
        </div>,
        document.body
      )}

      {/* Collection Modal */}
      <CollectionModal
        isOpen={showCollectionModal}
        onClose={() => {
          setShowCollectionModal(false);
          setSelectedCollectionId(undefined);
          setSelectedCollectionName('');
        }}
        collectionId={selectedCollectionId}
        initialName={selectedCollectionName}
        onSave={async (id, name, description, collectionType) => {
          if (!token) return;
          
          try {
            if (id) {
              // Update existing collection
              const response = await fetch(`${API_BASE_URL}/api/collections/${id}`, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  name,
                  description,
                  collection_type: collectionType
                })
              });

              if (response.ok) {
                setShowCollectionModal(false);
                setCollectionRefreshTrigger(prev => prev + 1);
                onCollectionsRefresh?.();
                window.dispatchEvent(new CustomEvent('collections-updated'));
              }
            } else {
              // Create new collection
              const response = await fetch(API_ENDPOINTS.CREATE_COLLECTION, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  name,
                  description,
                  collection_type: collectionType,
                  space_id: activeSpace?.id
                })
              });

              if (response.ok) {
                setShowCollectionModal(false);
                setCollectionRefreshTrigger(prev => prev + 1);
                onCollectionsRefresh?.();
                window.dispatchEvent(new CustomEvent('collections-updated'));
              }
            }
          } catch (error) {
            console.error('Failed to save collection:', error);
          }
        }}
        onShare={selectedCollectionId ? (id) => {
          setSharingCollectionId(id);
          setShowCollectionModal(false);
        } : undefined}
        onDelete={selectedCollectionId ? async (id, name) => {
          await handleDeleteCollection(id, name);
          setShowCollectionModal(false);
          setSelectedCollectionId(undefined);
        } : undefined}
      />

      {/* Collection Share Modal */}
      {sharingCollectionId !== null && (
        <CollectionShareModal
          isOpen={true}
          onClose={() => setSharingCollectionId(null)}
          collection={{
            id: sharingCollectionId,
            name: '' // Will be fetched by the modal
          }}
          onSuccess={() => {
            setSharingCollectionId(null);
            setCollectionRefreshTrigger(prev => prev + 1);
            onCollectionsRefresh?.();
            window.dispatchEvent(new CustomEvent('collections-updated'));
          }}
        />
      )}

      {/* Loose→Exact term resolve dialog */}
      {resolveDialogTerms && resolveDialogTerms.length > 0 && (
        <BindleTermResolveDialog
          looseTerms={resolveDialogTerms}
          onConfirm={handleResolveConfirm}
          onCancel={handleResolveCancel}
        />
      )}
    </div>
  );
};

export default UnifiedInventoryMenuBar;
